import type { ComponentProps, ReactNode } from 'react';
import type { DynamicToolUIPart, ToolUIPart } from 'ai';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  WrenchIcon,
  XCircleIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export type ToolPart = ToolUIPart | DynamicToolUIPart;
export type ToolProps = ComponentProps<typeof Collapsible>;

const statusLabels: Record<ToolPart['state'], string> = {
  'approval-requested': 'Validation requise',
  'approval-responded': 'Validation reçue',
  'input-available': 'En cours',
  'input-streaming': 'Préparation',
  'output-available': 'Terminé',
  'output-denied': 'Refusé',
  'output-error': 'Erreur',
};

const statusIcons: Record<ToolPart['state'], ReactNode> = {
  'approval-requested': <ClockIcon className="size-3.5 text-amber-600" />,
  'approval-responded': <CheckCircleIcon className="size-3.5 text-blue-600" />,
  'input-available': <ClockIcon className="size-3.5 animate-pulse text-muted-foreground" />,
  'input-streaming': <CircleIcon className="size-3.5 text-muted-foreground" />,
  'output-available': <CheckCircleIcon className="size-3.5 text-emerald-600" />,
  'output-denied': <XCircleIcon className="size-3.5 text-orange-600" />,
  'output-error': <XCircleIcon className="size-3.5 text-destructive" />,
};

function formatToolPayload(payload: unknown) {
  if (payload === undefined || payload === null || payload === '') {
    return null;
  }

  return typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
}

export const Tool = ({ className, ...props }: ToolProps) => (
  <Collapsible
    className={cn('group not-prose w-full rounded-[var(--radius-surface-xl)] border border-border/60 bg-muted/20', className)}
    {...props}
  />
);

export type ToolHeaderProps = {
  className?: string;
  state: ToolPart['state'];
  title?: string;
  toolName: string;
};

export const ToolHeader = ({
  className,
  state,
  title,
  toolName,
}: ToolHeaderProps) => (
  <CollapsibleTrigger
    className={cn('flex w-full items-center justify-between gap-3 px-3 py-2 text-left', className)}
  >
    <span className="flex min-w-0 items-center gap-2">
      <WrenchIcon className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate text-sm font-medium">{title ?? toolName}</span>
      <Badge variant="secondary" className="shrink-0 gap-1.5">
        {statusIcons[state]}
        {statusLabels[state]}
      </Badge>
    </span>
    <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
  </CollapsibleTrigger>
);

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>;

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn('border-t border-border/50 px-3 py-3 text-xs text-muted-foreground', className)}
    {...props}
  />
);

export function ToolPayload({
  label,
  payload,
}: {
  label: string;
  payload: unknown;
}) {
  const formattedPayload = formatToolPayload(payload);

  if (!formattedPayload) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="font-medium text-muted-foreground">{label}</p>
      <pre className="max-h-56 overflow-auto rounded-lg bg-background/80 p-3 text-[11px] leading-5 text-foreground">
        {formattedPayload}
      </pre>
    </div>
  );
}
